import React, { useRef } from "react"
import NextButton from "./NextButton"
import BackButton from "./BackButton"

type UploadFieldType = {
    onChange: (files: FileList | null) => void
    files: File[] | null
    onBack?: () => void
    onNext: () => void
    submit: boolean
    onSubmit: () => void
}

export default function UploadField(props: UploadFieldType) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const MAX_FILES = 5

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const existingFileCount = props.files ? props.files.length : 0
            const newFileCount = e.target.files.length
            const totalFileCount = existingFileCount + newFileCount

            // Check if adding new files would exceed the limit
            if (totalFileCount > MAX_FILES) {
                alert(`Maximal ${MAX_FILES} Dateien erlaubt. Du hast bereits ${existingFileCount} Datei(en) ausgewählt.`)
                e.target.value = ""
                return
            }

            // If there are existing files, merge them with the new ones
            if (props.files && props.files.length > 0) {
                const dt = new DataTransfer()
                // Add existing files
                props.files.forEach((file) => {
                    dt.items.add(file)
                })
                // Add new files
                Array.from(e.target.files).forEach((file) => {
                    dt.items.add(file)
                })
                props.onChange(dt.files)
            } else {
                // No existing files, just use the new ones
                props.onChange(e.target.files)
            }
            // Clear the input value so the same file can be selected again
            e.target.value = ""
        }
    }

    const handleClick = () => {
        const currentFileCount = props.files ? props.files.length : 0
        if (currentFileCount >= MAX_FILES) {
            alert(`Maximal ${MAX_FILES} Dateien erlaubt. Bitte entferne eine Datei, um eine neue hinzuzufügen.`)
            return
        }
        fileInputRef.current?.click()
    }

    const removeFile = (index: number) => {
        if (props.files) {
            const dt = new DataTransfer()
            props.files.forEach((file, i) => {
                if (i !== index) {
                    dt.items.add(file)
                }
            })
            props.onChange(dt.files)
        }
    }

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return "0 Bytes"
        const k = 1024
        const sizes = ["Bytes", "KB", "MB", "GB"]
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i]
    }

    const currentFileCount = props.files ? props.files.length : 0
    const isMaxFilesReached = currentFileCount >= MAX_FILES

    return (
        <div className="flex flex-col items-end w-full">
            <div className="flex flex-col py-8 gap-8 w-full">
                <div className="relative w-full">
                    <div
                        onClick={handleClick}
                        className={`w-full bg-base-100 border-b pb-4 transition-all duration-300 ${
                            isMaxFilesReached
                                ? "border-b-primary/30 cursor-not-allowed opacity-60"
                                : "border-b-primary cursor-pointer hover:border-b-secondary group"
                        }`}>
                        <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileChange} className="hidden" />
                        <div className="flex items-center justify-between">
                            <span
                                className={`text-2xl uppercase transition-colors duration-300 ${
                                    isMaxFilesReached ? "text-primary/60" : "text-primary group-hover:text-secondary"
                                }`}>
                                {props.files && props.files.length > 0
                                    ? `${props.files.length}/${MAX_FILES} Datei(en) ausgewählt`
                                    : "Anschreiben, Lebenslauf und Dokumente hochladen (PDF, PNG, JPG) *"}
                            </span>
                            <div
                                className={`text-4xl transition-colors duration-300 ${
                                    isMaxFilesReached ? "text-primary/60" : "text-primary group-hover:text-secondary"
                                }`}>
                                +
                            </div>
                        </div>
                    </div>
                    <div className="absolute bottom-0 left-0 h-[1px] w-full bg-secondary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>

                {props.files && props.files.length > 0 && (
                    <div className="flex flex-col gap-4 w-full">
                        {Array.from(props.files).map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between bg-base-100/50 p-4 rounded-lg border border-primary/20 hover:border-secondary/50 transition-all duration-300">
                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                    <span className="text-lg truncate">{file.name}</span>
                                    <span className="text-sm text-primary/60">{formatFileSize(file.size)}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="ml-4 text-2xl text-primary hover:text-error transition-colors duration-300 flex-shrink-0"
                                    aria-label="Datei entfernen">
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <p className="text-sm text-primary/60">
                    Erlaubte Formate: PDF, PNG, JPG, JPEG | Max. Größe: 5 MB pro Datei | Max. {MAX_FILES} Dateien
                </p>
            </div>

            <div className="flex gap-4 w-full sm:w-fit">
                {props.onBack && <BackButton onBack={props.onBack} />}
                <NextButton
                    filled={props.files !== null && props.files.length > 0}
                    submit={props.submit}
                    onNext={props.onNext}
                    onSubmit={props.onSubmit}
                />
            </div>
        </div>
    )
}
